import { FilterOption, VariableFilter } from "../components/common/Filters/Filters"
import { LabValue } from "../types/LabReport"

export type TestValueChartData = Array<{
  id: string
  data: Array<{ x: Date; y: number | null; isAbnormal?: boolean }>
}>

export const testValuesToChartData = (
  values: LabValue["values"],
  min: number = 0,
  max?: number,
): TestValueChartData => {
  const isAbnormal = (value: number) => {
    if (max === undefined) {
      return value < min
    }
    return value < min || value > max
  }

  // Sort values by date to ensure proper chronological order
  const sortedValues = [...values].sort(
    (a, b) => new Date(a.resultAt).getTime() - new Date(b.resultAt).getTime(),
  )

  const data: TestValueChartData = [
    {
      id: "values",
      data: sortedValues.map((value) => {
        const resultAt = new Date(value.resultAt)
        const result = Number(value.result)
        return {
          x: resultAt,
          y: result,
          isAbnormal: isAbnormal(result),
        }
      }),
    },
  ]

  return data
}

export interface FilterConfig {
  key: string
  title: string
}

// Get filter options gets all key/value pairs from input data to be used for
// client-side filtering
export const getFilterOptions = <T>(data: T[], filterConfigs?: FilterConfig[]): FilterOption[] => {
  const filterOptionsMap = new Map<string, any[]>()
  const configMap = new Map<string, string>()

  // Create a map of key to title for quick lookup
  if (filterConfigs) {
    filterConfigs.forEach((config) => {
      configMap.set(config.key, config.title)
    })
  }

  for (const item of data) {
    for (const key in item) {
      // Skip key if not included in filter configs
      if (filterConfigs && !configMap.has(key)) {
        continue
      }

      const values = filterOptionsMap.get(key) || []
      if (!values.includes(item[key])) {
        filterOptionsMap.set(key, [...values, item[key]])
      }
    }
  }

  // Turn map into filter options array
  const filterOptions: FilterOption[] = Object.entries(Object.fromEntries(filterOptionsMap)).map(
    ([key, values]) => ({
      key,
      title: configMap.get(key) || key, // Use title from config or fallback to key
      values,
    }),
  )

  return filterOptions
}

// Apply filters to data
export const filterData = <T>(data: T[], filters: VariableFilter[]): T[] => {
  if (filters.length === 0) {
    return data
  }

  // Ignore incomplete filters
  filters = filters.filter((filter) => {
    if (!filter.key || !filter.value) {
      return false
    }
    return true
  })

  return data.filter((item) => {
    for (const filter of filters) {
      if (filter.operator === "is") {
        // @ts-ignore
        if (item[filter.key] !== filter.value) {
          return false
        }
      } else if (filter.operator === "is not") {
        // @ts-ignore
        if (item[filter.key] === filter.value) {
          return false
        }
      }
    }
    return true
  })
}
